import { useCallback, useEffect, useRef, useState } from 'react'

import { type InlineEditTextFieldStatus } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import messages from './messages'
import {
  type PODisclaimerLabelEditableField,
  useMutateEditPODisclaimerLabel,
} from '../../models'

const RESOLVED_DISPLAY_DURATION_MS = 2000

export interface UseEditablePODisclaimerLabelTextFieldPresenterParams {
  label: PODisclaimerLabel
  field: PODisclaimerLabelEditableField
  onSuccess?: () => void
}

export const useEditablePODisclaimerLabelTextFieldPresenter = ({
  label,
  field,
  onSuccess,
}: UseEditablePODisclaimerLabelTextFieldPresenterParams) => {
  const intl = useProcurifyIntl()
  const persistedValue = label[field]
  const [inputValue, setInputValue] = useState(persistedValue)
  const [status, setStatus] = useState<InlineEditTextFieldStatus>('idle')
  const isMounted = useRef(true)
  const isCommittingRef = useRef(false)
  const resolvedStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const { mutateAsync } = useMutateEditPODisclaimerLabel()

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (resolvedStatusTimeoutRef.current) {
        clearTimeout(resolvedStatusTimeoutRef.current)
        resolvedStatusTimeoutRef.current = null
      }
    }
  }, [])

  // Keep inputValue in sync if the label prop changes externally (refetch)
  useEffect(() => {
    setInputValue(persistedValue)
  }, [persistedValue])

  const handleCommit = useCallback(async () => {
    if (isCommittingRef.current) return
    isCommittingRef.current = true

    try {
      const trimmedInputValue = inputValue.trim()

      // No-op if value unchanged
      if (trimmedInputValue === persistedValue) {
        return
      }

      // Both fields are required server-side — revert instead of PUTting an
      // empty value
      if (trimmedInputValue === '') {
        setInputValue(persistedValue)
        return
      }

      setStatus('pending')

      try {
        await mutateAsync({ label, field, value: trimmedInputValue })

        if (!isMounted.current) return

        setStatus('resolved')

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage),
        })

        onSuccess?.()

        resolvedStatusTimeoutRef.current = setTimeout(() => {
          if (!isMounted.current) return
          setStatus('idle')
          resolvedStatusTimeoutRef.current = null
        }, RESOLVED_DISPLAY_DURATION_MS)
      } catch (err) {
        if (!isMounted.current) return

        setStatus('rejected')
        setInputValue(persistedValue)

        const errorMessage = getMessageFromError(err)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage || intl.formatMessage(messages.errorFallback),
        })
      }
    } finally {
      isCommittingRef.current = false
    }
  }, [inputValue, persistedValue, label, field, mutateAsync, intl, onSuccess])

  const handleEscape = useCallback(() => {
    setInputValue(persistedValue)
    setStatus('idle')
  }, [persistedValue])

  const handleChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  return {
    data: {
      inputValue,
      status,
    },
    handlers: {
      handleChange,
      handleCommit,
      handleEscape,
    },
  }
}
